/**
 * The widths of the shell's two side panes.
 *
 * The stylesheet keeps the defaults: a width is stored only once the reviewer
 * has actually dragged one, so a first visit gets the mockup's proportions and
 * the responsive breakpoint keeps narrowing the panes until somebody overrides
 * it by hand.
 */

/** Mirrors `minmax(420px, 1fr)` on the reader column in `gap-review.css`. */
const READER_MIN_WIDTH = 420;

const STORAGE_KEY_PREFIX = 'gap-review:pane-width:';

export interface PaneWidthBounds {
  min: number;
  max: number;
}

export const QUEUE_WIDTH_BOUNDS: PaneWidthBounds = { min: 260, max: 560 };
export const RAIL_WIDTH_BOUNDS: PaneWidthBounds = { min: 300, max: 620 };

/**
 * Clamps to the pane's own bounds and then to what the viewport can hold.
 *
 * The second clamp is what stops a drag from breaking the page: the shell is
 * two fixed grid columns around one flexible reader, so a pane wide enough to
 * push the reader under its minimum does not squeeze it — it overflows the
 * window sideways. Splitting the leftover in half rather than subtracting the
 * other pane's live width keeps both panes' maxima reachable regardless of
 * which one is dragged first.
 */
export function clampPaneWidth(
  width: number,
  bounds: PaneWidthBounds,
  viewportWidth: number
): number {
  const viewportMax = (viewportWidth - READER_MIN_WIDTH) / 2;
  const upperBound = Math.max(bounds.min, Math.min(bounds.max, viewportMax));
  return Math.round(Math.min(Math.max(width, bounds.min), upperBound));
}

/**
 * The width the reviewer asked for, unclamped — it was chosen against whatever
 * window they had open at the time, and clamping it here would quietly rewrite
 * the preference to fit today's window. {@link clampPaneWidth} is applied on
 * the way to the DOM instead, so a wide window still gets the wide pane back.
 *
 * `undefined` means "no override": the stylesheet's own default stands.
 */
export function readStoredPaneWidth(paneKey: string): number | undefined {
  const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${paneKey}`);
  if (stored === null) return undefined;

  const parsedWidth = Number.parseInt(stored, 10);
  return Number.isNaN(parsedWidth) ? undefined : parsedWidth;
}

/** Passing `undefined` clears the override rather than storing a default. */
export function writeStoredPaneWidth(
  paneKey: string,
  width: number | undefined
): void {
  const storageKey = `${STORAGE_KEY_PREFIX}${paneKey}`;
  if (width === undefined) {
    localStorage.removeItem(storageKey);
    return;
  }
  localStorage.setItem(storageKey, String(width));
}
